import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { isTerminal } from '@platform/schema/generation';

import { elapsedOf, formatDuration, stageOf } from './stage';

import type { GenerationDetail } from '@platform/schema/generation';

/** Ids as plain strings: the SDK's detail carries them unbranded. */
interface Lesson {
  rowId: string;
  lessonId?: string;
  name: string;
}

type Job = Pick<
  GenerationDetail,
  'label' | 'status' | 'lessons' | 'startedAt' | 'finishedAt' | 'progressAt'
> & {
  current?: Omit<
    NonNullable<GenerationDetail['current']>,
    'rowId' | 'lessonId'
  > & {
    lessonId?: string;
  };
  completedLessons: readonly Lesson[];
  failedLessons: readonly Lesson[];
  remainingLessons: readonly Lesson[];
};

/** What the job is doing now, in the words `stage.ts` uses for a row. */
function doing(job: Job): string {
  // Before `current`: cancel leaves a running row to finish, and the job is still canceled.
  if (job.status === 'CANCELLED') return 'canceled';
  if (job.current !== undefined) return stageOf(job.current) ?? 'working';
  if (job.status === 'QUEUED') return 'waiting to start';
  return isTerminal(job.status) ? 'finished' : 'starting';
}

/**
 * A time, and its age while the job runs. The caller's clock stops when the job settles,
 * so an age printed after that would freeze at the moment it stopped.
 */
function lastUpdate(job: Job, now: number): string {
  if (job.progressAt === undefined) return '—';
  const at = new Date(job.progressAt).toLocaleTimeString();
  return isTerminal(job.status)
    ? at
    : `${at} (${formatDuration(now - Date.parse(job.progressAt))} ago)`;
}

const named = (lesson: Pick<Lesson, 'lessonId' | 'name'>): string =>
  lesson.lessonId === undefined
    ? lesson.name
    : `${lesson.lessonId} · ${lesson.name}`;

function LessonList({
  title,
  lessons,
}: {
  title: string;
  lessons: readonly Lesson[];
}) {
  return (
    <Box sx={{ minWidth: 200 }}>
      <Typography variant="subtitle2" component="h3">
        {title} ({lessons.length})
      </Typography>
      {lessons.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {lessons.map((lesson) => (
            <Typography component="li" variant="body2" key={lesson.rowId}>
              {named(lesson)}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * Where one job stands — CURR-29, read off the fields CURR-27 stores: the lesson it is on,
 * what it is doing, how long it has run and how long since it last moved. The last is the
 * one that tells a slow job from a stuck one, which a spinner never could.
 */
export function JobNow({ job, now }: { job: Job; now: number }) {
  const { current } = job;
  const lesson =
    current?.lessonName === undefined
      ? undefined
      : named({ ...current, name: current.lessonName });

  return (
    <Stack spacing={1} role="status" aria-label={`${job.label} now`}>
      <Typography variant="body2">
        {job.label}: {job.completedLessons.length} of {job.lessons.total}{' '}
        lessons done
      </Typography>
      <Typography variant="body2">
        {lesson === undefined ? 'Now' : `Now on ${lesson}`}: {doing(job)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Elapsed {elapsedOf(job, now) ?? '—'} · last update{' '}
        {lastUpdate(job, now)}
      </Typography>
      <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
        <LessonList title="Completed" lessons={job.completedLessons} />
        <LessonList title="Failed" lessons={job.failedLessons} />
        <LessonList title="Remaining" lessons={job.remainingLessons} />
      </Stack>
    </Stack>
  );
}
